import { Settings } from "../interfaces/settings.interface";

/**
 * EnvironmentService - Singleton service for loading environment-specific settings
 * 
 * This service determines the current environment from the URL and loads
 * the appropriate settings.json file. It follows the pattern used in MOBLADMWeb.
 * 
 * Environment Detection (in priority order):
 * - http://localhost → 'local' environment
 * - *.lcl or *.local domains → 'local' environment (for local development machines)
 * - /avirpfg_dev/ → 'dev' environment (pathname match)
 * - /avirpfg_syst/ → 'syst' environment (pathname match)
 * - /avirpfg_uat/ → 'uat' environment (pathname match)
 * - mobiledev.* hostname → 'dev' environment (hostname fallback)
 * - mobilesyst.* hostname → 'syst' environment (hostname fallback)
 * - mobileuat.* hostname → 'uat' environment (hostname fallback)
 * - /avirpfg/ → 'prod' environment (default)
 */
export class EnvironmentService {
    private static instance: EnvironmentService;

    private readonly baseName: string = 'avirpfg';
    private readonly env: string = '';

    private constructor() {
        const pathname = globalThis.location.pathname;
        const hostname = globalThis.location.hostname;
        const href = globalThis.location.href;

        // Detect if running on localhost or local domain (.lcl or .local)
        const isLocalDomain = href.startsWith('https://localhost') ||
            hostname.includes('.lcl') ||
            hostname.includes('.local');

        if (isLocalDomain) {
            // For local domains, always use /avirpfg as the base
            // This matches the local settings file and nginx config
            this.env = 'local';
            this.baseName = '/avirpfg';
        } else {
            // For non-local domains, extract environment from pathname
            // Example: /avirpfg_dev/ or /avirpfg_dev -> extract 'dev'
            // Make trailing slash optional to handle both cases
            const envRegex = /\/avirpfg_(\w+)/;
            const envMatch = envRegex.exec(pathname);

            if (envMatch?.[1]) {
                this.env = envMatch[1];
                this.baseName = `/avirpfg_${this.env}`;
            } else {
                // Pathname has no environment suffix (e.g. /avirpfg)
                // Fall back to hostname-based detection
                const envFromHost = this.detectEnvFromHostname(hostname);
                this.env = envFromHost;
                this.baseName = '/avirpfg';
            }
        }
    }

    /**
     * Detect environment from hostname when pathname doesn't contain an env suffix.
     * Matches the hostname patterns used across PennDOT deployments:
     *   mobiledev.penndot.pa.gov → dev
     *   mobilesyst.penndot.pa.gov → syst
     *   mobileuat.penndot.pa.gov → uat
     *   mobile.penndot.pa.gov → prod (default)
     */
    private detectEnvFromHostname(hostname: string): string {
        const lower = hostname.toLowerCase();
        if (lower.startsWith('mobiledev')) return 'dev';
        if (lower.startsWith('mobilesyst')) return 'syst';
        if (lower.startsWith('mobileuat')) return 'uat';
        return 'prod';
    }

    /**
     * Get the singleton instance of EnvironmentService
     */
    public static getInstance(): EnvironmentService {
        if (!this.instance) {
            this.instance = new EnvironmentService();
        }
        return this.instance;
    }

    /**
     * Get the current environment name (dev, syst, uat, prod, local)
     */
    public getEnvironment(): string {
        return this.env;
    }

    /**
     * Get the base path/context root for the application
     */
    public getBaseName(): string {
        return this.baseName;
    }

    /**
     * Get the settings file name for the current environment
     */
    public getSettingsFileName(): string {
        return `settings.${this.env}.json`;
    }

    /**
     * Load settings from the appropriate JSON file for the current environment
     * @returns Promise<Settings> The loaded settings object
     */
    public getSettings(): Promise<Settings> {
        const url = `${this.baseName}/assets/settings/${this.getSettingsFileName()}`;
        return fetch(url).then(response => {
            if (!response.ok) {
                throw new Error(`Failed to load settings from ${url}: ${response.statusText}`);
            }
            return response.json();
        });
    }
}
